import { MonoTypeOperatorFunction, Observable, timer } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  repeat,
  RepeatConfig,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { ArrayFunctions } from './array.functions';
import { ObjectFunctions } from './object.functions';

export class ObservableFunctions {
  public static distinctUntilSomeKeyChanged<T extends object>() {
    return distinctUntilChanged<T>(ObjectFunctions.deepEquals);
  }

  public static repeatUntil<T>(
    until: (value: T) => boolean,
    repeatConfig: number[] | number | RepeatConfig
  ): MonoTypeOperatorFunction<T> {
    const SECOND = 1000;

    const config = Array.isArray(repeatConfig)
      ? {
          count: repeatConfig.length + 1,
          delay: (retryCount) => timer(repeatConfig[retryCount - 1] * SECOND),
        }
      : repeatConfig;

    return (source$: Observable<T>) =>
      source$.pipe(
        repeat(config),
        first((data) => until(data))
      );
  }

  public static confirm<T>(confirmFn: (s?: T) => Observable<boolean>) {
    return (source$: Observable<T>): Observable<T> =>
      source$.pipe(
        switchMap((s) =>
          confirmFn(s).pipe(
            take(1),
            filter((confirmed) => !!confirmed)
          )
        ),
        withLatestFrom(source$),
        map(([_, source]) => source)
      );
  }

  public static on<T extends string | { type: string } = string>(events: string | string[]) {
    const eventsArr = ArrayFunctions.wrapIntoArray(events);
    return (source$: Observable<T>): Observable<T> =>
      source$.pipe(
        filter((s) =>
          eventsArr.some((event) => {
            if (typeof s === 'string') return event === s;
            return event === s.type;
          })
        )
      );
  }
}
